import { Link } from "react-router-dom";
import { ArrowLeft, MessageSquare, Phone, Mail, HelpCircle } from "lucide-react";
import Footer from "../components/Footer";

export default function SMSTermsPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-[#00B894] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <img src="/images/gigseasy-logo.svg" alt="GigsEasy" className="h-10 mb-6" />
          <h1 className="text-4xl font-bold text-slate-900 mb-2">SMS Terms</h1>
          <p className="text-slate-500">Last Updated: March 20, 2026</p>
        </div>

        <p className="text-slate-600 mb-8">
          Terms and conditions for SMS messaging services provided by GigsEasy.com, a service owned and operated by{" "}
          <strong>Active Consumers LLC</strong>.
        </p>

        {/* Quick Info Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="bg-slate-50 p-4 rounded-lg text-center">
            <MessageSquare className="w-6 h-6 text-[#00B894] mx-auto mb-2" />
            <p className="font-semibold text-slate-900 text-sm">Short Code</p>
            <p className="text-xs text-slate-500">GIGSY (TBD)</p>
          </div>
          <div className="bg-slate-50 p-4 rounded-lg text-center">
            <p className="font-semibold text-slate-900 text-sm mt-2">Program</p>
            <p className="text-xs text-slate-500">GigsEasy</p>
          </div>
          <div className="bg-slate-50 p-4 rounded-lg text-center">
            <p className="font-semibold text-slate-900 text-sm mt-2">Frequency</p>
            <p className="text-xs text-slate-500">Up to 4/month</p>
          </div>
          <div className="bg-slate-50 p-4 rounded-lg text-center">
            <p className="font-semibold text-slate-900 text-sm mt-2">Pricing</p>
            <p className="text-xs text-slate-500">Msg & Data rates may apply</p>
          </div>
        </div>

        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">User Opt-In</h2>
            <p className="text-slate-600 mb-4">
              By signing up at{" "}
              <Link to="/register" className="text-[#00B894] hover:underline">https://gigseasy.com/register</Link>{" "}
              and opting in to SMS notifications, you consent to receive SMS messages from GigsEasy, including:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Task assignment notifications</li>
              <li>Account-related alerts and status updates</li>
              <li>Payment and withdrawal confirmations</li>
              <li>Occasional promotional messages about new features</li>
            </ul>
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg mb-4">
              <p className="text-amber-800 text-sm">
                <strong>Important:</strong> Messages are <strong>not</strong> sent using an automatic telephone dialing system. 
                Consent to receive SMS messages is <strong>not</strong> a condition of purchase or use of the GigsEasy Service.
              </p>
            </div>
            <p className="text-slate-600">
              All information collected during the SMS opt-in process will <strong>not</strong> be sold, rented, or shared 
              with third parties for marketing purposes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">User Opt-Out</h2>
            <p className="text-slate-600 mb-4">To stop receiving SMS messages at any time:</p>
            <div className="bg-slate-100 p-4 rounded-lg mb-4">
              <p className="font-semibold text-slate-900">Text "STOP" to GIGSY (TBD)</p>
            </div>
            <p className="text-slate-600 mb-4">
              You may also reply with "QUIT," "END," "CANCEL," "UNSUBSCRIBE," or "STOP ALL."
            </p>
            <p className="text-slate-600">
              Once you opt out, you will no longer receive SMS messages from GigsEasy. You may also disable SMS 
              notifications in your account settings.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Help & Support</h2>
            <p className="text-slate-600 mb-4">For assistance with SMS messaging:</p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <HelpCircle className="w-5 h-5 text-[#00B894] mb-2" />
                <p className="font-semibold text-slate-900 text-sm">Text Help</p>
                <p className="text-slate-600 text-sm">HELP to GIGSY (TBD)</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <Mail className="w-5 h-5 text-[#00B894] mb-2" />
                <p className="font-semibold text-slate-900 text-sm">Email</p>
                <a href="mailto:support@gigseasy.com" className="text-[#00B894] text-sm hover:underline">support@gigseasy.com</a>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <Phone className="w-5 h-5 text-[#00B894] mb-2" />
                <p className="font-semibold text-slate-900 text-sm">Phone</p>
                <a href="tel:+18667015753" className="text-[#00B894] text-sm hover:underline">(866) 701-5753</a>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">User Fees</h2>
            <p className="text-slate-600">
              This is a standard-rate messaging service. <strong>Message and data rates may apply</strong> based on your 
              mobile carrier's plan. GigsEasy does not charge any additional fees for SMS messages beyond what your 
              carrier may charge.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Supported Carriers (U.S. Only)</h2>
            <p className="text-slate-600 mb-4">
              Our SMS service is supported by most major U.S. carriers, including but not limited to:
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {["AT&T", "T-Mobile", "Verizon Wireless", "Sprint", "Boost Mobile", "MetroPCS", "U.S. Cellular", 
                "Cricket", "Virgin Mobile", "Straight Talk"].map((carrier) => (
                <span key={carrier} className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm">
                  {carrier}
                </span>
              ))}
            </div>
            <p className="text-slate-500 text-sm italic">
              Carriers are not responsible for delayed or undelivered messages.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Types of Messages</h2>
            <p className="text-slate-600 mb-4">
              When you opt in to SMS notifications, you may receive the following types of messages:
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-slate-200 rounded-lg text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-slate-900">Message Type</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-900">Description</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-900">Frequency</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  <tr>
                    <td className="px-4 py-3 text-slate-700 font-medium">Task Notifications</td>
                    <td className="px-4 py-3 text-slate-600">New task assignments and reminders</td>
                    <td className="px-4 py-3 text-slate-600">As needed</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-slate-700 font-medium">Account Alerts</td>
                    <td className="px-4 py-3 text-slate-600">Security alerts, password changes, account status</td>
                    <td className="px-4 py-3 text-slate-600">As needed</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-slate-700 font-medium">Payment Updates</td>
                    <td className="px-4 py-3 text-slate-600">Withdrawal confirmations, payment status</td>
                    <td className="px-4 py-3 text-slate-600">Per transaction</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-slate-700 font-medium">Promotional</td>
                    <td className="px-4 py-3 text-slate-600">New features, special offers (optional)</td>
                    <td className="px-4 py-3 text-slate-600">Up to 2/month</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Warranty Disclaimer</h2>
            <p className="text-slate-600 mb-4">
              GigsEasy is not responsible for any delays in SMS delivery, as delivery depends on your wireless carrier's 
              network. We make reasonable efforts to deliver messages promptly, but cannot guarantee delivery times or 
              delivery at all due to factors beyond our control.
            </p>
            <div className="bg-slate-100 border-l-4 border-[#00B894] p-4 rounded-r-lg">
              <p className="text-slate-700 text-sm">
                <strong>You acknowledge and agree that SMS notifications are a convenience feature and should not be 
                your sole method of tracking tasks and account activity.</strong> We recommend also checking your 
                dashboard and email for important notifications.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Privacy Policy</h2>
            <p className="text-slate-600 mb-4">
              Your privacy is important to us. The phone number and information you provide for SMS services is used{" "}
              <strong>only</strong> to deliver requested communications and manage your account.
            </p>
            <p className="text-slate-600 mb-4">
              We will not sell, rent, or share your phone number with third parties for marketing purposes. We may 
              disclose information only when legally required (e.g., by court order) or when necessary to protect our 
              rights or property.
            </p>
            <p className="text-slate-600">
              For more information, please review our full{" "}
              <Link to="/privacy-policy" className="text-[#00B894] hover:underline">Privacy Policy</Link>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Dispute Resolution</h2>
            <p className="text-slate-600 mb-4">
              All disputes arising from these SMS Terms will be resolved through <strong>binding arbitration</strong> in 
              accordance with the American Arbitration Association's rules, as described in our{" "}
              <Link to="/terms-of-use" className="text-[#00B894] hover:underline">Terms of Use</Link>.
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Arbitration will be conducted by a single arbitrator, whose decision is final and binding</li>
              <li>Both parties waive the right to participate in any class action or jury trial</li>
              <li>Each party will bear their own costs unless the arbitrator awards fees to the prevailing party</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Miscellaneous</h2>
            <p className="text-slate-600 mb-4">
              These SMS Terms, together with our{" "}
              <Link to="/terms-of-use" className="text-[#00B894] hover:underline">Terms of Use</Link> and{" "}
              <Link to="/privacy-policy" className="text-[#00B894] hover:underline">Privacy Policy</Link>, form the 
              entire agreement between you and GigsEasy regarding SMS communications.
            </p>
            <p className="text-slate-600">
              Updates or modifications to these SMS Terms will be posted at https://gigseasy.com/sms-terms and are 
              effective immediately unless otherwise noted. Your continued use of SMS services constitutes acceptance 
              of any updated Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Contact Us</h2>
            <p className="text-slate-600 mb-4">For any questions about these SMS Terms:</p>
            <div className="bg-slate-50 p-6 rounded-lg">
              <p className="font-semibold text-slate-900">Active Consumers LLC</p>
              <p className="text-slate-600">Email: support@gigseasy.com</p>
              <p className="text-slate-600">Phone: (866) 701-5753</p>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
